import React from "react";
import {
  MAX_PERCENT_DIFF,
  MAX_TRANSACTIONS,
  useAutoPredictPrice,
} from "../../hooks/pricePredictions.hooks";
import { AppraiserProperty } from "../../types/appraiser.types";
import { ConfirmationDialog } from "../dialogs/ConfirmationDialog";
import { Button, Radio, RadioGroup } from "@mui/material";
import { CheckboxInput } from "common/components/form/CheckboxInput";
import { SmallNumberInput } from "common/components/form/SmallNumberInput";
import { Typography } from "common/components/Typography";
import { useNavigate } from "react-router-dom";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";

interface AutoPricePredictionProps {
  property: AppraiserProperty;
  buttonText?: string;
  skipNavigation?: boolean;
}

export const AutoPricePrediction: React.FC<AutoPricePredictionProps> = ({
  property,
  buttonText,
  skipNavigation,
}) => {
  const { t } = useTranslation();
  const { autoPredictPrice, isLoading } = useAutoPredictPrice();

  const [confirmationOpen, setConfirmationOpen] = React.useState(false);

  type Method = "AVOID_OUTLIERS" | "DEFAULT";

  const [selectedMethod, setSelectedMethod] = React.useState<Method>("DEFAULT");
  const [filterOutRelatedRE, setFilterOutRelatedRE] = React.useState(true);
  const [includeOlderProperties, setIncludeOlderProperties] =
    React.useState(false);

  const [maxTransactionsToBuy, setMaxTransactionsToBuy] =
    React.useState(MAX_TRANSACTIONS);

  const handleChange = (event: any) => {
    setSelectedMethod(event.target.value);
  };

  const navigate = useNavigate();
  return (
    <>
      <Button
        onClick={() => {
          setConfirmationOpen(true);
        }}
        variant="outlined"
      >
        {buttonText ?? t("autoPricePrediction.evaluateAutomatically")}
      </Button>

      <ConfirmationDialog
        title={t("autoPricePrediction.automaticValue")}
        isOpen={confirmationOpen}
        onClose={() => {
          if (isLoading) return;
          setConfirmationOpen(false);
        }}
        hideDoNotShowAgain
        actionButtonText={t("autoPricePrediction.calculate")}
        isLoading={isLoading}
        onConfirm={async () => {
          await autoPredictPrice(property, {
            avoidOutliers: selectedMethod === "AVOID_OUTLIERS",
            maxTransactions: maxTransactionsToBuy,
            filterOutRelatedRE: filterOutRelatedRE,
            noMinYear: includeOlderProperties,
          });
          setConfirmationOpen(false);
          if (!skipNavigation) {
            navigate("comparables/summary");
          }
        }}
        width="650px"
      >
        <RadioGroup onChange={handleChange} value={selectedMethod}>
          <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
            <Radio value="DEFAULT" />
            <p>{t("autoPricePrediction.allowPurchase")}</p>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "10px",
              alignItems: "start",
            }}
          >
            <Radio
              style={{ marginTop: "5px" }}
              onChange={handleChange}
              value="AVOID_OUTLIERS"
            />
            <div>
              <div
                style={{ display: "flex", flexDirection: "row", gap: "10px" }}
              >
                <p>{t("autoPricePrediction.allowOptimalSelection")}</p>
                <SmallNumberInput
                  value={maxTransactionsToBuy}
                  onChange={(value) => {
                    setMaxTransactionsToBuy(value);
                    setSelectedMethod("AVOID_OUTLIERS");
                  }}
                  minValue={3}
                  maxValue={20}
                  customStyle={{ width: 120, marginLeft: 10 }}
                />
              </div>
              <Typography variant="caption">
                {t("autoPricePrediction.transactionsLimit")}
              </Typography>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              marginTop: "15px",
              marginLeft: "10px",
            }}
          >
            <CheckboxInput
              label={t("autoPricePrediction.includeRelatedRE")}
              isChecked={!filterOutRelatedRE}
              onChange={() => setFilterOutRelatedRE(!filterOutRelatedRE)}
            />
            <CheckboxInput
              label={t("autoPricePrediction.includeOlderProperties")}
              isChecked={includeOlderProperties}
              onChange={() => setIncludeOlderProperties(!includeOlderProperties)}
            />
          </div>

          <p
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "10px",
              marginLeft: "20px",
            }}
          >
            <WarningAmberIcon color="warning" />
            {t("autoPricePrediction.warning")}
          </p>
        </RadioGroup>
      </ConfirmationDialog>
    </>
  );
};
